// Het reisgezelschap (`ft_travel_party`) lezen IN DE BROWSER
// (privacy-audit 26-10-2026).
//
// De homepage, categorie-, onderwerp-, bestemmings- en dealpagina's gaan met
// `cache-control: public` naar Cloudflare, en Cloudflare neemt de cookie niet
// mee in de cachesleutel. Las de server de cookie (getTravelParty), dan kwam
// het gezelschap van één bezoeker, met de geboortedata van de kinderen, in de
// gedeelde HTML voor iedereen. Daarom rendert de server die pagina's zonder
// cookie, en neemt de client het gezelschap pas na de mount over.
//
// Gebruik getTravelParty() (server) alleen op pagina's die nooit gedeeld
// gecachet worden, zoals /booking/ (NEVER_CACHE_PREFIXES in edge-cache.js).

import { useEffect } from "react";
import {
  TRAVEL_PARTY_COOKIE_NAME,
  parseTravelPartyCookie,
} from "./travel-party-cookie.js";

/**
 * Het gezelschap uit een `document.cookie`-string, of null.
 * @param {string} cookieString
 */
export function readTravelPartyFromCookieString(cookieString) {
  if (!cookieString || typeof cookieString !== "string") return null;
  const prefix = `${TRAVEL_PARTY_COOKIE_NAME}=`;
  const entry = cookieString
    .split(/;\s*/)
    .find((c) => c.startsWith(prefix));
  if (!entry) return null;
  try {
    return parseTravelPartyCookie(decodeURIComponent(entry.slice(prefix.length)));
  } catch {
    // Kapotte codering: zelfde als geen cookie.
    return null;
  }
}

/** Het gezelschap uit de cookie van deze browser, of null (ook op de server). */
export function readTravelPartyFromDocument() {
  if (typeof document === "undefined") return null;
  return readTravelPartyFromCookieString(document.cookie);
}

/**
 * Roept `onParty(party)` één keer aan na de mount, als de browser een
 * geldig gezelschap in de cookie heeft. De eerste render (server én
 * hydratie) ziet dus nooit de cookie: de HTML blijft voor iedereen gelijk.
 *
 * @param {(party: object) => void} onParty
 */
export function useTravelPartyFromCookie(onParty) {
  useEffect(() => {
    const party = readTravelPartyFromDocument();
    if (party) onParty(party);
    // Bewust alleen na de mount: latere schrijfacties naar de cookie komen
    // van dit component zelf en staan dan al in de state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
